import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Webhook, Copy, Send, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function WebhookCard({ sub, onChange }) {
  const wh = sub.webhook || {};
  const [url, setUrl] = useState(wh.url || "");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [last, setLast] = useState(wh.last_delivery || null);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/subaccounts/${sub.id}/webhook`, { url });
      toast.success(url ? "Webhook saved" : "Webhook disabled");
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/subaccounts/${sub.id}/webhook/test`);
      setLast(data);
      if (data.ok) toast.success(`Test delivered (HTTP ${data.status_code})`);
      else toast.error(`Test failed: ${data.error || "HTTP " + data.status_code}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Card className="p-5 border-border/70 space-y-4">
      <div className="flex items-center gap-2">
        <Webhook className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Fulfillment Webhook</h3>
        {wh.configured && <span className="text-[10px] font-mono text-emerald-400">ACTIVE</span>}
      </div>
      <p className="text-xs text-muted-foreground">
        We POST an <code className="font-mono text-foreground">order.fulfillment_updated</code> event to this URL
        (server-to-server) every time an order's fulfillment changes, so your app can update without a reload.
      </p>

      <div className="space-y-1.5">
        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Endpoint URL (https)</Label>
        <div className="flex gap-2">
          <Input data-testid="webhook-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourapp.com/paygate/webhook" className="font-mono text-sm" />
          <Button data-testid="webhook-save-btn" onClick={save} disabled={busy} className="shrink-0">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {wh.configured && wh.secret && (
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Signing Secret</Label>
          <div className="flex items-center gap-2 rounded-md border border-border/70 bg-muted/30 px-3 py-2">
            <code data-testid="webhook-secret" className="flex-1 truncate font-mono text-xs">{wh.secret}</code>
            <button type="button" data-testid="webhook-copy-secret" onClick={() => copy(wh.secret, "Secret")} className="text-muted-foreground hover:text-primary">
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground font-mono">
            Verify each request: HMAC-SHA256 of the raw body with this secret == <code className="text-foreground">X-PayGate-Signature</code> header.
          </p>
        </div>
      )}

      {wh.configured && (
        <div className="flex items-center gap-3">
          <Button variant="outline" data-testid="webhook-test-btn" onClick={sendTest} disabled={testing} className="gap-2">
            <Send className="h-4 w-4" /> {testing ? "Sending…" : "Send test event"}
          </Button>
          {last && (
            <span data-testid="webhook-last-delivery" className={`flex items-center gap-1 text-xs font-mono ${last.ok ? "text-emerald-400" : "text-destructive"}`}>
              {last.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {last.ok ? `Delivered · HTTP ${last.status_code}` : `Failed · ${last.error || "HTTP " + last.status_code}`}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
